import { addDays, differenceInCalendarDays } from "date-fns";

import { Coupon } from "types/Coupons";
import { BillingPeriodUnit } from "types/BillingPeriodUnit";
import Plan from "types/Plan";
import { fmtCurrency, fmtCurrencyCompact } from "./index";
import Messages from "misc/Messages";


export const canUsePremium = (planSlug: string | undefined) => {
  return planSlug !== "free" && planSlug !== undefined;
}

export const canUseUltraPremium = (planSlug: string | undefined) => {
  return planSlug !== "free" && planSlug !== undefined;
}

export const isEnterprise = (planSlug: string | undefined) => {
  return planSlug === 'custom' || planSlug?.startsWith("enterprise");
};


export const manualRenewToThisIfScheduled = (planSlug: string | undefined) => {
  return !!(planSlug && (isEnterprise(planSlug) || [ "custom", "extended_trial" ].includes(planSlug)));
};

/**
 * @deprecated Plan information contains a 'customizable' flag, should check that one instead of this helper function
 * @param planSlug
 */
export const isUsingChargebeeCustomFields = (planSlug: string | undefined) => {
  return !!(planSlug && (planSlug.startsWith("enterprise") || [ "custom", "extended_trial" ].includes(planSlug)));
};

/**
 * @deprecated Plan information should contain this as a flag.
 * @param planSlug
 */
export const hasToContactUsBeforeChange = (planSlug: string | undefined) => {
  return !!(planSlug && (planSlug.startsWith("enterprise") || [ "custom", "extended_trial" ].includes(planSlug)));
};

export const isLegacyAnnualPlan = (planSlug: string | undefined) => {
  return planSlug?.startsWith("annual_");
};

export const isYearlyPlan = (planSlug: string | undefined) => {
  return planSlug?.startsWith("yearly_");
}

export function getMonthlyEquivalent(planSlug: string | undefined) {
  if (isYearlyPlan(planSlug)) {
    return planSlug?.substring("yearly_".length);
  }
  if (isLegacyAnnualPlan(planSlug)) {
    return planSlug?.substring("annual_".length);
  }

  return planSlug;
}

export const getBillingPeriodMultiplier = (period1: number, periodUnit1: BillingPeriodUnit, period2: number, periodUnit2: BillingPeriodUnit) => {
  const periodMultipliers = {
    month: 1,
    year: 12 // 12 months in a year
  };

  return (periodMultipliers[periodUnit2] * period2) / (periodMultipliers[periodUnit1] * period1);
};

export const getMonthlyPriceInUSD = (plan: Plan) => {
  if (plan.billingPeriod === 1 && plan.billingPeriodUnit === "month") {
    return Math.round(plan.price / 100);
  }

  // Convert FROM plan's billing period TO monthly (swap params to get correct 1/12 multiplier for yearly plans)
  return Math.round((plan.price * getBillingPeriodMultiplier(plan.billingPeriod, plan.billingPeriodUnit, 1, "month")) / 100);
};

export const getMonthlyCredits = (plan: Plan) => {
  if (plan.billingPeriod === 1 && plan.billingPeriodUnit === "month") {
    return plan.apiCreditLimit;
  }

  return Math.round(plan.apiCreditLimit * getBillingPeriodMultiplier(plan.billingPeriod, plan.billingPeriodUnit, 1, "month"));
}

// 2x monthly price cap from the fraud-prevention rule, floored at the $6 minimum so the
// cap input always has a satisfiable range. $0-priced plans (test/internal Enterprise or
// Custom setups) land on $6 rather than rendering an unsatisfiable max=0.
export const getMaxSpendingCap = (plan: Plan | null | undefined): number | undefined => {
  if (!plan) return undefined;
  return Math.max(6, 2 * getMonthlyPriceInUSD(plan));
};

// Monthly-equivalent API credits so cross-period comparisons read like-for-like.
// `yearly_*` plans store the annual total in apiCreditLimit (e.g. yearly_hobby_2 = 1,200,000);
// monthly and legacy `annual_*` plans already store monthly values.
/**
 * @deprecated  we don't offer monthly credits for annual plans anymore, the whole pool is given to the user immediately
 * @param plan
 */
export const getMonthlyCreditsEquivalent = (plan: Plan): number => {
  if (isYearlyPlan(plan.planSlug)) {
    return Math.round(plan.apiCreditLimit / 12);
  }
  return plan.apiCreditLimit;
};

export const getDiscountPriceFor = (plan: Pick<Plan, "planSlug" | "price"> | null | undefined, coupons: Coupon[] | null | undefined) => {
  if (!plan) {
    return 0;
  }

  if (!coupons || (coupons.length === 0)) {
    return plan.price;
  }

  // use applicable coupons only
  const matchingCoupons = coupons.filter(c => !c.applicable_plan_ids || c.applicable_plan_ids.includes(plan.planSlug));

  // TODO Chargebee supports "line level" and "invoice level" coupons which can affect the calculation below, but we don't check such differences here
  //  if we want to deal with it, "line level" coupons should be applied first (both fix amount and percentage), followed by the invoice level coupons
  let discountPercentage = 0;
  let discountAmount = 0;

  for (const coupon of matchingCoupons) {
    if (coupon.discount_type === "percentage") {
      discountPercentage += coupon.discount_percentage || coupon.discount_value!;
    } else if (coupon.discount_type === "fixed_amount") {
      discountAmount += coupon.discount_amount || coupon.discount_value!;
    }
  }

  return ((plan.price - discountAmount) * (100 - discountPercentage)) / 100;
};

export const getCouponDiscountText = (coupon: Coupon) => {
  if (coupon.discount_type === "percentage") {
    return `-${ coupon.discount_value! }%`;
  } else if (coupon.discount_type === "fixed_amount") {
    return `-${ fmtCurrency(coupon.discount_value!) }`; // TODO should also pass the currency code of the coupon here
  }
};

export type PlanCategory =
  | 'free-trial'
  | 'free'
  | 'monthly-plg'
  | 'annual-plg'
  | 'monthly-slg'
  | 'annual-slg'
  | 'other';

export const getPlanCategory = (planSlug: string | undefined, isFreeTrial: boolean = false): PlanCategory => {
  if (!planSlug) {
    return 'free';
  }

  // Free Trial
  if (isFreeTrial) {
    return 'free-trial';
  }

  // Free plan
  if (planSlug === 'free') {
    return 'free';
  }

  // SLG plans (Custom, Enterprise) - scaling/professional are PLG!
  const isSLG = planSlug.startsWith('enterprise') ||
                ['custom'].some(prefix => planSlug.includes(prefix));

  if (isSLG) {
    return isLegacyAnnualPlan(planSlug) || isYearlyPlan(planSlug) ? 'annual-slg' : 'monthly-slg';
  }

  // PLG plans (Hobby, Startup, Business, Professional/Scaling)
  const isPLG = ['hobby', 'business', 'startup', 'professional', 'scaling', 'advanced'].some(name => planSlug.includes(name));

  if (isPLG) {
    return isLegacyAnnualPlan(planSlug) || isYearlyPlan(planSlug) ? 'annual-plg' : 'monthly-plg';
  }

  // Other plans (mini, extended_trial, etc.)
  return 'other';
};

export const USAGE_WARNING_THRESHOLD = 0.70;

export const calculateUsagePercentage = (current: number, max: number): number => {
  if (!max || max === 0) return 0;
  return current / max;
};

/**
 * Returns the appropriate usage warning message based on which limits are being approached.
 * Returns null if no warning is needed (both usages below threshold).
 */
export const getUsageAlertMessage = (
  apiCreditsUsage: number,
  concurrencyUsage: number,
  threshold: number = USAGE_WARNING_THRESHOLD
): string | null => {
  const isApiCreditsHigh = apiCreditsUsage >= threshold;
  const isConcurrencyHigh = concurrencyUsage >= threshold;

  if (!isApiCreditsHigh && !isConcurrencyHigh) {
    return null;
  }

  if (isApiCreditsHigh && isConcurrencyHigh) {
    return Messages.billingV2.usageWarnings.approachingBoth;
  }

  if (isConcurrencyHigh) {
    return Messages.billingV2.usageWarnings.approachingConcurrency;
  }

  return Messages.billingV2.usageWarnings.approachingCredits;
};

// Map plan slug to its tier for looking up tier-specific features.
export const getPlanTier = (planSlug: string): string => {
  if (planSlug === "free") return "free";
  if (planSlug.includes("hobby")) return "hobby";
  if (planSlug.includes("startup")) return "startup";
  if (planSlug.includes("business")) return "business";
  if (planSlug.includes("advanced")) return "advanced";
  // Legacy professional_1 / professional_202503 are grandfathered Scaling; only _202604 is the new Professional tier.
  if (planSlug.includes("professional") && planSlug.includes("_202604")) return "professional";
  if (planSlug.includes("professional") || planSlug.includes("scaling")) return "scaling";
  return "free";
};


/**
 * Format a plan's display price as a string.
 * Yearly plans: per-month equivalent with 10% annual discount.
 * Monthly plans: price as-is.
 * All prices returned as "/month".
 */
// TODO plan data does contain a fake 10% discount coupon for annual plans. should change the price calculation to use
//  the coupon info instead of a hardcoded -10%
export const formatPlanDisplayPrice = (
  priceInCents: number,
  currencyCode: string = "USD",
  planSlug: string,
): string => {
  if (isYearlyPlan(planSlug)) {
    const perMonthCents = priceInCents / 12 * 0.9;
    return fmtCurrencyCompact(perMonthCents / 100, currencyCode);
  }
  return fmtCurrencyCompact(priceInCents / 100, currencyCode);
};

// --- Billing Redesign (v2) ---

// Free trial duration in days — matches backend (User.ts isFreeTrial check)
const FREE_TRIAL_DAYS = 7;

export const formatRegionLabel = (geo: string | null | undefined): string => {
  if (geo === "All Geotargeting") return "Geotargeting: Global";
  if (geo === "US & EU Geotargeting") return "Geotargeting: US & EU Only";
  return "Geotargeting: US & EU Only";
};

/**
 * Calculate free trial end date and remaining days from the user's sign-up date.
 * Returns null if signedUpAt is not provided.
 */
export const getTrialInfo = (signedUpAt: string | undefined) => {
  if (!signedUpAt) return null;
  const trialEndDate = addDays(new Date(signedUpAt), FREE_TRIAL_DAYS);
  const daysRemaining = differenceInCalendarDays(trialEndDate, new Date());
  return {
    trialEndDate,
    trialEndTimestamp: Math.floor(trialEndDate.getTime() / 1000),
    daysRemaining,
  };
};
